import type { ValidationError } from "@/generatedApi";

const IGNORED_CODES = [
	"xsd/1839",
	"xsd/unexpected-element",
	"xsd/attribute-required-but-missing",
];

/**
 * Convert a jsonPath from the backend (e.g. "$..Object.Objectsoort")
 * to a FormKit node path (e.g. "Object.Objectsoort").
 */
export function jsonPathToFormKitPath(jsonPath?: string): string | undefined {
	return jsonPath?.slice(3).replaceAll("[", ".").replaceAll("]", "");
}

/**
 * Split validation errors into form-level errors (not mappable to a FormKit field)
 * and field-level errors (mappable to a FormKit field).
 *
 * @param validationErrors - All validation errors from the backend
 * @param hasFormKitNode - Function that checks if a FormKit path resolves to a node
 */
export function splitFormLevelErrors(
	validationErrors: ValidationError[],
	hasFormKitNode: (path: string) => boolean,
): ValidationError[] {
	return validationErrors.filter((err) => {
		if (IGNORED_CODES.includes(err.code)) return false;
		const path = jsonPathToFormKitPath(err.jsonPath);
		return !path || !hasFormKitNode(path);
	});
}

/**
 * Filter out ignored validation errors, returning only visible/actionable errors.
 */
export function getVisibleErrors(
	validationErrors: ValidationError[],
): ValidationError[] {
	return validationErrors.filter((err) => !IGNORED_CODES.includes(err.code));
}

/**
 * Group form-level errors by code prefix for display in the sidebar.
 */
export function groupFormLevelErrors(formLevelErrors: ValidationError[]): {
	providerErrors: ValidationError[];
	generalErrors: ValidationError[];
} {
	return {
		providerErrors: formLevelErrors.filter((e) => e.code.startsWith("xml/")),
		generalErrors: formLevelErrors.filter((e) => e.code.startsWith("xsd/")),
	};
}
